import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './ATMVaultManager.css';

// Helper function to format countdown display
const formatCountdown = (hoursRemaining, minutesRemaining, isExpired) => {
  if (isExpired) {
    return <span style={{ color: '#ff4444', fontWeight: 'bold', fontSize: '10px' }}>⏰ EXPIRED</span>;
  }
  
  if (hoursRemaining === null || minutesRemaining === null) {
    return <span style={{ color: '#888', fontSize: '10px' }}>No expiration</span>;
  }
  
  const color = hoursRemaining < 1 ? '#ff6644' : hoursRemaining < 6 ? '#ffaa44' : '#44ff44';
  
  if (hoursRemaining > 0) {
    return <span style={{ color, fontSize: '10px' }}>⏰ {hoursRemaining}h {minutesRemaining}m left</span>;
  } else {
    return <span style={{ color, fontSize: '10px' }}>⏰ {minutesRemaining}m left</span>;
  }
};

const ATMVaultManager = ({ 
  isPlacingVault, 
  setIsPlacingVault, 
  vaultPlacementData, 
  setVaultPlacementData,
  onVaultChanged = () => {} // Callback to refresh vault display on map
}) => {
  const { user, updateUser } = useAuth();
  const [vaults, setVaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVault, setSelectedVault] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showAddCodeModal, setShowAddCodeModal] = useState(false);
  const [showAddBalanceModal, setShowAddBalanceModal] = useState(false);
  const [userATMCodes, setUserATMCodes] = useState([]);
  
  // Form states
  const [vaultLocation, setVaultLocation] = useState({ lat: '', lng: '' });
  const [selectedATMCode, setSelectedATMCode] = useState('');
  const [balanceAmount, setBalanceAmount] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    
    // Only fetch data if user is fully authenticated and loaded
    if (user && user.token && user.username && user._id) {
      fetchUserVaults();
      fetchUserATMCodes();
    } else if (user === null) {
      // User is explicitly null (not logged in)
      setLoading(false);
      setError('Please log in to view your vaults');
    } else {
      // User object exists but is not fully loaded yet
      setLoading(true);
    }
  }, [user]);

  // Handle vault placement data from map click
  useEffect(() => {
    if (vaultPlacementData) {
      const newLocation = {
        lat: vaultPlacementData.lat.toFixed(6),
        lng: vaultPlacementData.lng.toFixed(6)
      };
      setVaultLocation(newLocation);
      
      // If we're placing a new vault, create it immediately
      if (!selectedVault) {
        createVaultFromMapClick(newLocation);
      } else {
        // If we're moving an existing vault, move it immediately
        moveVaultFromMapClick(newLocation);
      }
      
      // Clear the placement data after using it
      setVaultPlacementData(null);
    }
  }, [vaultPlacementData, selectedVault, setVaultPlacementData]);

  const fetchUserVaults = async () => {
    try {
      const token = user?.token || localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.get('/atm-vaults/my-vaults');

      
      const data = response.data;
      setVaults(data.vaults || []);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('Error fetching vaults:', error);
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 503) {
        setError('Service temporarily unavailable. Please try again later.');
      } else {
        setError(error.response?.data?.message || error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserATMCodes = async () => {
    try {
      const token = user?.token || localStorage.getItem('token');
      if (!token) return;

      const response = await api.get('/auth/me');
      const userData = response.data;
      setUserATMCodes(userData.atmCodes?.filter(code => !code.used) || []);
    } catch (error) {
      console.error('Error fetching ATM codes:', error);
      if (error.response?.status === 401 || error.response?.status === 503) {
        console.log('fetchUserATMCodes: Skipping due to auth/service error:', error.response?.status);
        return;
      }
    }
  };

  const createVault = async () => {
    try {
      if (!vaultLocation.lat || !vaultLocation.lng) {
        alert('Please enter valid coordinates');
        return;
      }

      const response = await api.post('/atm-vaults/create', {
        lat: parseFloat(vaultLocation.lat),
        lng: parseFloat(vaultLocation.lng)
      });
      const data = response.data;

      alert('Vault created successfully!');
      setShowCreateModal(false);
      setVaultLocation({ lat: '', lng: '' });
      fetchUserVaults();
      onVaultChanged(); // Refresh map display
    } catch (error) {
      console.error('Error creating vault:', error);
      alert(error.response?.data?.message || error.message);
    }
  };

  // Create vault directly from map click
  const createVaultFromMapClick = async (location) => {
    try {
      const response = await api.post('/atm-vaults/create', {
        lat: parseFloat(location.lat),
        lng: parseFloat(location.lng)
      });
      const data = response.data;

      alert(`✅ Vault created successfully at ${location.lat}, ${location.lng}!`);
      setIsPlacingVault(false);
      setVaultLocation({ lat: '', lng: '' });
      fetchUserVaults();
      onVaultChanged(); // Refresh map display
    } catch (error) {
      console.error('Error creating vault:', error);
      alert(error.response?.data?.message || error.message);
      setIsPlacingVault(false);
    }
  };

  const moveVault = async () => {
    try {
      if (!selectedVault || !vaultLocation.lat || !vaultLocation.lng) {
        alert('Please enter valid coordinates');
        return;
      }

      if (!user?.token) {
        alert('Please log in to move vaults');
        return;
      }

      const response = await api.put(`/atm-vaults/${selectedVault.vaultId}/move`, {
        lat: parseFloat(vaultLocation.lat),
        lng: parseFloat(vaultLocation.lng)
      });
      const data = response.data;

      alert('Vault moved successfully!');
      setShowMoveModal(false);
      setVaultLocation({ lat: '', lng: '' });
      setSelectedVault(null);
      
      // Update user balance if provided
      if (data.userBalance !== undefined) {
        updateUser({ wrCoins: data.userBalance });
      }
      
      fetchUserVaults();
      onVaultChanged(); // Refresh map display
    } catch (error) {
      console.error('Error moving vault:', error);
      alert(error.response?.data?.message || error.message);
    }
  };

  // Move vault directly from map click
  const moveVaultFromMapClick = async (location) => {
    try {
      if (!user?.token) {
        alert('Please log in to move vaults');
        return;
      }
      
      const response = await api.put(`/atm-vaults/${selectedVault.vaultId}/move`, {
        lat: parseFloat(location.lat),
        lng: parseFloat(location.lng)
      });
      const data = response.data;

      alert(`✅ Vault moved successfully to ${location.lat}, ${location.lng}! Cost: ${getMoveCost()} WR Coins`);
      setIsPlacingVault(false);
      setVaultLocation({ lat: '', lng: '' });
      setSelectedVault(null);
      
      // Update user balance if provided
      if (data.userBalance !== undefined) {
        updateUser({ wrCoins: data.userBalance });
      }
      
      fetchUserVaults();
      onVaultChanged(); // Refresh map display
    } catch (error) {
      console.error('Error moving vault:', error);
      alert(error.response?.data?.message || error.message);
      setIsPlacingVault(false);
      setSelectedVault(null);
    }
  };

  const addATMCode = async () => {
    try {
      if (!selectedVault || !selectedATMCode) {
        alert('Please select an ATM code');
        return;
      }

      if (!user?.token) {
        alert('Please log in to add ATM codes');
        return;
      }

      const response = await api.post(`/atm-vaults/${selectedVault.vaultId}/add-code`, {
        atmId: selectedATMCode
      });
      const data = response.data;

      alert('ATM code added to vault successfully!');
      setShowAddCodeModal(false);
      setSelectedATMCode('');
      setSelectedVault(null);
      fetchUserVaults();
      onVaultChanged(); // Refresh map display
      fetchUserATMCodes();
    } catch (error) {
      console.error('Error adding ATM code:', error);
      alert(error.response?.data?.message || error.message);
    }
  };

  const addBalance = async () => {
    try {
      if (!selectedVault || !balanceAmount || parseFloat(balanceAmount) <= 0) {
        alert('Please enter a valid amount');
        return;
      }

      const amount = parseFloat(balanceAmount);
      if (amount > user.wrCoins) {
        alert('Insufficient WR Coins balance');
        return;
      }

      if (!user?.token) {
        alert('Please log in to add balance');
        return;
      }

      const response = await api.post(`/atm-vaults/${selectedVault.vaultId}/add-balance`, {
        amount: amount
      });
      const data = response.data;

      alert('Balance added to vault successfully!');
      setShowAddBalanceModal(false);
      setBalanceAmount('');
      setSelectedVault(null);
      updateUser({ wrCoins: data.userBalance });
      fetchUserVaults();
      onVaultChanged(); // Refresh map display
    } catch (error) {
      console.error('Error adding balance:', error);
      alert(error.response?.data?.message || error.message);
    }
  };


  // Check if vault is within 1000m withdrawal range
  const isVaultInRange = (vault) => {
    if (!user?.computerLocation || user.computerLocation.lat === null || user.computerLocation.lng === null) return false;
    
    const computerLocation = user.computerLocation;
    const vaultLocation = vault.coordinates;
    
    // Calculate distance using Haversine formula
    const R = 6371000; // Earth's radius in meters
    const dLat = (vaultLocation.lat - computerLocation.lat) * Math.PI / 180;
    const dLng = (vaultLocation.lng - computerLocation.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(computerLocation.lat * Math.PI / 180) * Math.cos(vaultLocation.lat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return distance <= 1000; // 1000m withdrawal radius
  };

  // Get distance to vault
  const getDistanceToVault = (vault) => {
    if (!user?.computerLocation || user.computerLocation.lat === null || user.computerLocation.lng === null) return 'Unknown';
    
    const computerLocation = user.computerLocation;
    const vaultLocation = vault.coordinates;
    
    // Calculate distance using Haversine formula
    const R = 6371000; // Earth's radius in meters
    const dLat = (vaultLocation.lat - computerLocation.lat) * Math.PI / 180;
    const dLng = (vaultLocation.lng - computerLocation.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(computerLocation.lat * Math.PI / 180) * Math.cos(vaultLocation.lat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    if (distance < 1000) {
      return `${Math.round(distance)}m`;
    } else {
      return `${(distance / 1000).toFixed(1)}km`;
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getMovementStatus = (vault) => {
    // Check if vault is locked due to SWAT
    if (vault.isLocked && vault.lockedUntil && new Date() < new Date(vault.lockedUntil)) {
      return '🔒 Locked (SWAT activity)';
    }
    
    // Check weekly movement restriction
    if (vault.weeklyMovementUsed && vault.lastWeeklyMoveAt) {
      const now = new Date();
      const oneWeekAfterMove = new Date(new Date(vault.lastWeeklyMoveAt).getTime() + (7 * 24 * 60 * 60 * 1000));
      
      if (now < oneWeekAfterMove) {
        const hoursRemaining = Math.ceil((oneWeekAfterMove - now) / (1000 * 60 * 60));
        const daysRemaining = Math.ceil(hoursRemaining / 24);
        return `⏰ Weekly cooldown: ${daysRemaining} day(s) remaining`;
      }
    }
    
    return '🟢 Ready to move (once per week)';
  };

  const canMoveVault = (vault) => {
    // Check if vault is locked due to SWAT
    if (vault.isLocked && vault.lockedUntil && new Date() < new Date(vault.lockedUntil)) return false;
    
    // Check weekly movement restriction
    if (vault.weeklyMovementUsed && vault.lastWeeklyMoveAt) {
      const now = new Date();
      const oneWeekAfterMove = new Date(new Date(vault.lastWeeklyMoveAt).getTime() + (7 * 24 * 60 * 60 * 1000));
      
      if (now < oneWeekAfterMove) {
        return false;
      }
    }
    
    return true;
  };

  const getMoveCost = () => {
    return 100; // Fixed cost of 100 WR Coins for weekly vault movement
  };

  // Check if user has available moves
  const hasAvailableMoves = () => {
    if (!user?.dailyComputerMoves) return true; // If no move data, assume moves available
    if (user.dailyComputerMoves.moves >= 3) return false;
    if (user.dailyComputerMoves.lockoutUntil && new Date() < new Date(user.dailyComputerMoves.lockoutUntil)) return false;
    return true;
  };

  const getNextMoveCost = () => {
    if (!user?.dailyComputerMoves) return 50;
    const moves = user.dailyComputerMoves.moves || 0;
    return 50 * Math.pow(2, moves);
  };

  const getRemainingMoves = () => {
    if (!user?.dailyComputerMoves) return 3;
    return Math.max(0, 3 - (user.dailyComputerMoves.moves || 0));
  };

  const deleteATMCode = async (vaultId, atmId) => {
    try {
      if (!window.confirm('Are you sure you want to delete this ATM code from your vault?')) {
        return;
      }

      const response = await api.delete(`/atm-vaults/${vaultId}/delete-code`, {
        data: { atmId: atmId }
      });
      const data = response.data;

      alert('ATM code deleted from vault successfully!');
      fetchUserVaults();
      onVaultChanged(); // Refresh map display
    } catch (error) {
      console.error('Error deleting ATM code:', error);
      alert(error.response?.data?.message || error.message);
    }
  };

  // Move computer near ATM location
  const moveComputerNearATM = async (atmId, atmCode, fromVault = false, vaultId = null) => {
    try {
      if (!hasAvailableMoves()) {
        alert('No computer moves available! You have used all 3 moves for today.');
        return;
      }

      const moveCost = getNextMoveCost();
      if (user.wrCoins < moveCost) {
        alert(`Insufficient WR Coins. You need ${moveCost} WR Coins to move the computer.`);
        return;
      }

      if (!user?.computerLocation || !user.computerLocation.isActive) {
        alert('You must have an active computer deployed to move near ATM locations.');
        return;
      }

      if (!window.confirm(`Move your computer near this ATM location? This will cost ${moveCost} WR Coins and use 1 computer move (${getRemainingMoves()} remaining).`)) {
        return;
      }

      const response = await api.post('/treasure-hunt/move-computer-near-atm', {
        atmId,
        atmLocation: {
          lat: atmCode.coordinates.coordinates ? atmCode.coordinates.coordinates[1] : atmCode.coordinates.lat,
          lng: atmCode.coordinates.coordinates ? atmCode.coordinates.coordinates[0] : atmCode.coordinates.lng
        }
      });
      const data = response.data;

      alert(`✅ Computer moved near ATM location! Cost: ${moveCost} WR Coins. Moves remaining: ${getRemainingMoves() - 1}/3. Your computer is now within range of the ATM.`);
      
      // Update user context with new balance and move count
      updateUser({ 
        wrCoins: data.userBalance,
        dailyComputerMoves: data.dailyComputerMoves,
        computerLocation: data.newComputerLocation
      });

      if (fromVault) {
        fetchUserVaults();
        onVaultChanged();
      } else {
        fetchUserATMCodes();
      }
    } catch (error) {
      console.error('Error moving computer near ATM:', error);
      alert(error.message);
    }
  };

  // Tab rendering functions
  const renderOverviewTab = () => {
    const vault = vaults[0]; // User can only have one vault
    return (
      <div className="overview-tab">
        <div className="vault-summary-card">
          <div className="vault-header-info">
            <h3>🔐 Vault {vault.vaultId.split('_').pop()}</h3>
            <span className={`vault-status ${vault.isLocked ? 'locked' : 'active'}`}>
              {vault.isLocked ? '🔒 LOCKED' : '🟢 ACTIVE'}
            </span>
          </div>
          
          {vault.isLocked && vault.lockedUntil && new Date() < new Date(vault.lockedUntil) && (
            <div className="vault-locked-banner">
              🚨 Vault locked until: {formatDate(vault.lockedUntil)}
            </div>
          )}

          <div className="vault-overview-grid">
            <div className="overview-stat">
              <div className="stat-icon">📍</div>
              <div className="stat-info">
                <span className="stat-label">Location</span>
                <span className="stat-value">{vault.coordinates.lat.toFixed(4)}, {vault.coordinates.lng.toFixed(4)}</span>
              </div>
            </div>
            
            <div className="overview-stat">
              <div className="stat-icon">🏧</div>
              <div className="stat-info">
                <span className="stat-label">ATM Codes</span>
                <span className="stat-value">{vault.atmCodes?.length || 0}/4</span>
              </div>
            </div>
            
            <div className="overview-stat">
              <div className="stat-icon">💰</div>
              <div className="stat-info">
                <span className="stat-label">Balance</span>
                <span className="stat-value">{vault.storedBalance} WR</span>
              </div>
            </div>
            
            <div className="overview-stat">
              <div className="stat-icon">📅</div>
              <div className="stat-info">
                <span className="stat-label">Weekly Move</span>
                <span className="stat-value">{vault.weeklyMovementUsed ? 'Used' : 'Available'}</span>
              </div>
            </div>
            
            <div className="overview-stat">
              <div className="stat-icon">📏</div>
              <div className="stat-info">
                <span className="stat-label">Distance</span>
                <span className={`stat-value ${isVaultInRange(vault) ? 'in-range' : 'out-range'}`}>
                  {getDistanceToVault(vault)} {isVaultInRange(vault) ? '✅' : '❌'}
                </span>
              </div>
            </div>
            
            {canMoveVault(vault) && (
              <div className="overview-stat">
                <div className="stat-icon">💸</div>
                <div className="stat-info">
                  <span className="stat-label">Move Cost</span>
                  <span className="stat-value">{getMoveCost()} WR</span>
                </div>
              </div>
            )}
          </div>

          <div className="movement-status-info">
            <h4>🚶 Movement Status</h4>
            <span className="movement-indicator">{getMovementStatus(vault)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderContentsTab = () => {
    const vault = vaults[0];
    return (
      <div className="contents-tab">
        <div className="atm-codes-section">
          <h3>🏧 Stored ATM Codes ({vault.atmCodes?.length || 0}/4)</h3>
          {vault.atmCodes?.length > 0 ? (
            <div className="atm-codes-grid">
              {vault.atmCodes.map((code, index) => (
                <div key={index} className="atm-code-card">
                  <div className="code-header">
                    <span className="code-title">🔑 {code.code || 'Unknown Code'}</span>
                    {!code.atmId && (
                      <div className="debug-info" style={{color: 'red', fontSize: '10px'}}>
                        Debug: Missing atmId - {JSON.stringify(Object.keys(code))}
                      </div>
                    )}
                    <div className="code-actions">
                      <button
                        onClick={() => moveComputerNearATM(code.atmId || code.id || code._id, code, true, vault.vaultId)}
                        disabled={!hasAvailableMoves() || !user?.computerLocation?.isActive || !code.atmId}
                        className={`code-action-btn move-btn ${hasAvailableMoves() && user?.computerLocation?.isActive && code.atmId ? '' : 'disabled'}`}
                        title={
                          !code.atmId
                            ? 'ATM ID missing - cannot move'
                            : !hasAvailableMoves() 
                              ? `No moves available (${getRemainingMoves()}/3 remaining)` 
                              : !user?.computerLocation?.isActive 
                                ? 'Deploy computer first' 
                                : `Move computer near ATM location (Cost: ${getNextMoveCost()} WR Coins)`
                        }
                      >
                        📍
                      </button>
                      <button
                        onClick={() => deleteATMCode(vault.vaultId, code.atmId || code.id || code._id)}
                        disabled={!code.atmId}
                        className={`code-action-btn delete-btn ${code.atmId ? '' : 'disabled'}`}
                        title={code.atmId ? "Delete this ATM code from vault" : "Cannot delete - ATM ID missing"}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="code-details">
                    <p className="code-location">📍 {code.locationDescription || 'Unknown Location'}</p>
                    <div className="code-expiry">
                      {formatCountdown(code.hoursRemaining, code.minutesRemaining, code.isExpired)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-codes-message">
              <h4>No ATM codes stored</h4>
              <p>Add ATM codes to your vault for secure storage</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderActionsTab = () => {
    const vault = vaults[0];
    return (
      <div className="actions-tab">
        <div className="actions-grid">
          <div className="action-card">
            <div className="action-header">
              <h4>🏧 Add ATM Code</h4>
              <p>Store ATM codes securely in your vault</p>
            </div>
            <button 
              onClick={() => {
                setSelectedVault(vault);
                setShowAddCodeModal(true);
              }}
              disabled={vault.atmCodes?.length >= 4 || userATMCodes.length === 0}
              className={`action-button primary ${vault.atmCodes?.length >= 4 || userATMCodes.length === 0 ? 'disabled' : ''}`}
            >
              {vault.atmCodes?.length >= 4 ? 'Vault Full (4/4)' : userATMCodes.length === 0 ? 'No Codes Available' : 'Add Code'}
            </button>
          </div>

          <div className="action-card">
            <div className="action-header">
              <h4>💰 Add Balance</h4>
              <p>Deposit WR Coins for safekeeping</p>
            </div>
            <button 
              onClick={() => {
                setSelectedVault(vault);
                setShowAddBalanceModal(true);
              }}
              className="action-button success"
            >
              Deposit Coins
            </button>
          </div>


          <div className="action-card">
            <div className="action-header">
              <h4>📍 Move Vault</h4>
              <p>Relocate your vault on the map</p>
            </div>
            <button 
              onClick={() => {
                setSelectedVault(vault);
                setVaultLocation({ 
                  lat: vault.coordinates.lat.toString(), 
                  lng: vault.coordinates.lng.toString() 
                });
                // Navigate to Hack the Planet with vault move state
                window.location.href = '/hack-the-planet?vaultMove=true';
              }}
              disabled={!canMoveVault(vault)}
              className={`action-button info ${!canMoveVault(vault) ? 'disabled' : ''}`}
            >
              {!canMoveVault(vault) ? 'Weekly Move Used' : `Move (${getMoveCost()} WR)`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSecurityTab = () => {
    const vault = vaults[0];
    return (
      <div className="security-tab">
        <div className="security-overview">
          <h3>🔒 Vault Security Status</h3>
          
          <div className="security-stats">
            <div className="security-stat">
              <span className="security-label">🛡️ Protection Level:</span>
              <span className="security-value high">Advanced</span>
            </div>
            <div className="security-stat">
              <span className="security-label">🚨 SWAT Risk:</span>
              <span className="security-value medium">20% per hack attempt</span>
            </div>
            <div className="security-stat">
              <span className="security-label">🎯 Success Rate:</span>
              <span className="security-value low">60% base + contents modifier</span>
            </div>
          </div>

          {vault.hackHistory && vault.hackHistory.length > 0 && (
            <div className="hack-history">
              <h4>📊 Recent Hack Attempts</h4>
              <div className="hack-list">
                {vault.hackHistory.slice(-5).map((hack, index) => (
                  <div key={index} className={`hack-item ${hack.wasSuccessful ? 'successful' : 'failed'}`}>
                    <div className="hack-info">
                      <span className="hacker-name">👤 {hack.hackerUsername}</span>
                      <span className="hack-date">📅 {new Date(hack.hackedAt).toLocaleDateString()}</span>
                    </div>
                    <div className="hack-result">
                      <span className={`hack-status ${hack.wasSuccessful ? 'success' : 'failure'}`}>
                        {hack.wasSuccessful ? '✅ Successful' : '❌ Failed'}
                      </span>
                      {hack.swatCalled && <span className="swat-indicator">🚨 SWAT</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="security-tips">
            <h4>💡 Security Tips</h4>
            <ul>
              <li>🏃 Move your vault regularly to avoid predictable patterns</li>
              <li>💰 Don't store too many coins - it increases hack success rate</li>
              <li>🏧 Distribute ATM codes across multiple storage methods</li>
              <li>📍 Choose remote locations to reduce discovery chances</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="atm-vault-manager">
        <div className="loading">Loading ATM Vaults...</div>
      </div>
    );
  }

  return (
    <div className="atm-vault-manager">
      <div className="vault-header">
        <h2>🔐 ATM Code Vault</h2>
        <p>Store up to 4 ATM codes and WR Coins in your personal vault. You can only have one vault at a time.</p>
        <p><strong>📍 To place a vault:</strong> Click the button below, then click anywhere on the map to place your vault!</p>
        <button 
          className={`create-vault-btn ${isPlacingVault ? 'active-placement' : ''}`}
          onClick={() => setIsPlacingVault(true)}
          disabled={vaults.length >= 1 || isPlacingVault}
        >
          {isPlacingVault ? '🎯 Click on Map to Place Vault!' : vaults.length >= 1 ? '🔒 You already have a vault' : '🗺️ Place New Vault on Map'}
        </button>
        {isPlacingVault && (
          <div className="placement-instructions">
            <p style={{color: '#ffaa00', textAlign: 'center', margin: '10px 0', padding: '10px', background: 'rgba(255, 165, 0, 0.1)', border: '1px solid #ff6600', borderRadius: '4px'}}>
              💡 Now click anywhere on the map to place your vault at that location!
              <br />
              <span style={{ fontSize: '12px' }}>⚠️ Note: Vaults can only be placed on land, not in water/sea areas</span>
            </p>
            <button 
              className="cancel-placement-btn"
              onClick={() => setIsPlacingVault(false)}
              style={{display: 'block', margin: '0 auto', padding: '8px 16px', background: '#666', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
            >
              Cancel Placement
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      {vaults.length > 0 && (
        <div className="vault-tabs">
          <button
            className={`vault-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button
            className={`vault-tab ${activeTab === 'contents' ? 'active' : ''}`}
            onClick={() => setActiveTab('contents')}
          >
            🏧 ATM Codes
          </button>
          <button
            className={`vault-tab ${activeTab === 'actions' ? 'active' : ''}`}
            onClick={() => setActiveTab('actions')}
          >
            ⚙️ Actions
          </button>
          <button
            className={`vault-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            🔒 Security
          </button>
        </div>
      )}

      {/* Tab Content */}
      <div className="vault-tab-content">
        {vaults.length === 0 ? (
          <div className="no-vaults">
            <h3>🔐 No Vault Created</h3>
            <p>You haven't created your vault yet.</p>
            <p>Create your vault to start storing ATM codes and WR Coins securely!</p>
            <div className="vault-benefits">
              <h4>🔐 Vault Benefits:</h4>
              <ul>
                <li>🏧 Store up to 4 ATM codes safely</li>
                <li>💰 Keep WR Coins secure from other players</li>
                <li>📍 Move vault up to 3 times for strategic positioning</li>
                <li>🛡️ Protected by advanced security systems</li>
              </ul>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'contents' && renderContentsTab()}
            {activeTab === 'actions' && renderActionsTab()}
            {activeTab === 'security' && renderSecurityTab()}
          </>
        )}
      </div>

      {/* Create Vault Modal */}
      {showCreateModal && (
        <div className={`modal-overlay ${isPlacingVault ? 'placement-mode' : ''}`}>
          <div className={`modal ${isPlacingVault ? 'placement-mode' : ''}`}>
            <div className="modal-header">
              <h3>Create New Vault</h3>
              <button onClick={() => setShowCreateModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Location:</label>
                <button 
                  onClick={() => setIsPlacingVault(true)}
                  className={`map-select-btn ${isPlacingVault ? 'active' : ''}`}
                  disabled={isPlacingVault}
                >
                  {isPlacingVault ? '🎯 Click on Map to Select Location!' : '🗺️ Click Map to Select Location'}
                </button>
                {isPlacingVault && (
                  <div style={{
                    background: 'rgba(255, 165, 0, 0.1)',
                    border: '1px solid #ff6600',
                    borderRadius: '4px',
                    padding: '10px',
                    marginTop: '10px',
                    color: '#ffaa00',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                  }}>
                    💡 Click anywhere on the map to place your vault. This modal will stay visible so you can see your coordinates.
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Latitude:</label>
                <input
                  type="number"
                  step="any"
                  value={vaultLocation.lat}
                  onChange={(e) => setVaultLocation({...vaultLocation, lat: e.target.value})}
                  placeholder="e.g., 40.7128"
                />
              </div>
              <div className="form-group">
                <label>Longitude:</label>
                <input
                  type="number"
                  step="any"
                  value={vaultLocation.lng}
                  onChange={(e) => setVaultLocation({...vaultLocation, lng: e.target.value})}
                  placeholder="e.g., -74.0060"
                />
              </div>
              <div className="modal-actions">
                <button onClick={createVault} className="confirm-btn">Create Vault</button>
                <button onClick={() => setShowCreateModal(false)} className="cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move Vault Modal */}
      {showMoveModal && selectedVault && (
        <div className={`modal-overlay ${isPlacingVault ? 'placement-mode' : ''}`}>
          <div className={`modal ${isPlacingVault ? 'placement-mode' : ''}`}>
            <div className="modal-header">
              <h3>Move Vault</h3>
              <button onClick={() => setShowMoveModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-content">
              <p>Moving vault will cost {getMoveCost()} WR Coins</p>
              <div className="form-group">
                <label>New Location:</label>
                <button 
                  onClick={() => setIsPlacingVault(true)}
                  className={`map-select-btn ${isPlacingVault ? 'active' : ''}`}
                  disabled={isPlacingVault}
                >
                  {isPlacingVault ? '🎯 Click on Map to Select Location!' : '🗺️ Click Map to Select Location'}
                </button>
                {isPlacingVault && (
                  <div style={{
                    background: 'rgba(255, 165, 0, 0.1)',
                    border: '1px solid #ff6600',
                    borderRadius: '4px',
                    padding: '10px',
                    marginTop: '10px',
                    color: '#ffaa00',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                  }}>
                    💡 Click anywhere on the map to move your vault. This modal will stay visible so you can see your coordinates.
                    <br />
                    ⚠️ Note: Vaults can only be moved to land, not water/sea areas.
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>New Latitude:</label>
                <input
                  type="number"
                  step="any"
                  value={vaultLocation.lat}
                  onChange={(e) => setVaultLocation({...vaultLocation, lat: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>New Longitude:</label>
                <input
                  type="number"
                  step="any"
                  value={vaultLocation.lng}
                  onChange={(e) => setVaultLocation({...vaultLocation, lng: e.target.value})}
                />
              </div>
              <div className="modal-actions">
                <button onClick={moveVault} className="confirm-btn">Move Vault</button>
                <button onClick={() => setShowMoveModal(false)} className="cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add ATM Code Modal */}
      {showAddCodeModal && selectedVault && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add ATM Code to Vault</h3>
              <button onClick={() => setShowAddCodeModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-content">
              {userATMCodes.length === 0 ? (
                <p>You don't have any unused ATM codes to add.</p>
              ) : (
                <>
                  <div className="form-group">
                    <label>Select ATM Code:</label>
                    <select
                      value={selectedATMCode}
                      onChange={(e) => setSelectedATMCode(e.target.value)}
                    >
                      <option value="">Choose an ATM code...</option>
                      {userATMCodes.map((code, index) => (
                        <option key={index} value={code.atmId}>
                          {code.code} - {code.locationDescription}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-actions">
                    <button onClick={addATMCode} className="confirm-btn">Add Code</button>
                    <button onClick={() => setShowAddCodeModal(false)} className="cancel-btn">Cancel</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Balance Modal */}
      {showAddBalanceModal && selectedVault && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Balance to Vault</h3>
              <button onClick={() => setShowAddBalanceModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-content">
              <p>Your Balance: {user.wrCoins} WR Coins</p>
              <div className="form-group">
                <label>Amount to Store:</label>
                <input
                  type="number"
                  min="1"
                  max={user.wrCoins}
                  value={balanceAmount}
                  onChange={(e) => setBalanceAmount(e.target.value)}
                  placeholder="Enter amount..."
                />
              </div>
              <div className="modal-actions">
                <button onClick={addBalance} className="confirm-btn">Add Balance</button>
                <button onClick={() => setShowAddBalanceModal(false)} className="cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ATMVaultManager;